<template>
  <div
    class="file-input__container"
    @dragover.prevent="handleDragOver"
    @dragleave="handleDragLeave"
    @drop.prevent="handleFileDrop"
    :class="{ 'drag-over': isDragOver }"
  >
    <v-icon large color="grey">mdi-cloud-upload</v-icon>
    <BaseTypography variant="subtitle-3">
      {{ text }}
    </BaseTypography>
    <TextButton @click="triggerFileInput">{{ buttonText }}</TextButton>
    <BaseTypography variant="subtitle-2" regular>{{ helperText }}</BaseTypography>
    <template v-if="files.length">
      <FileStatus
        v-for="file in files"
        :key="file.name"
        :file="file"
        @itemClick="downloadFile(file)"
        @remove="removeFile(file)"
      />
    </template>
    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
    <v-file-input
      class="visually-hidden"
      ref="fileInput"
      :accept="accept"
      label="Wgraj plik"
      multiple
      :value="files"
      @change="addFile"
      :rules="isOptional ? [] : [required]"
    >
      <template #message>
        <AlertError>Dodaj przynajmniej jeden plik, aby zapisać usługę</AlertError>
      </template>
    </v-file-input>
  </div>
</template>

<script>
import BaseTypography from '@/component/Base/base-typography';
import TextButton from '@/component/Button/text-button';
import AlertError from '@/component/Alert/alert-error';
import FileStatus from './file-status';

export default {
  name: 'BaseFileUpload',
  components: {
    BaseTypography,
    TextButton,
    FileStatus,
    AlertError,
  },
  props: {
    isOptional: {
      type: Boolean,
      default: false,
    },
    files: {
      required: true,
    },
    text: {
      type: String,
      required: true,
    },
    helperText: {
      type: String,
    },
    accept: {
      required: true,
    },
    buttonText: {
      type: String,
      default: 'Wybierz plik',
    },
  },
  data() {
    return {
      isDragOver: false,
    };
  },
  methods: {
    required(e) {
      return e.length > 0 || 'Pole jest wymagane';
    },
    triggerFileInput() {
      this.$refs.fileInput.$el.querySelector('input').click();
    },
    handleDragOver() {
      this.isDragOver = true;
    },
    handleDragLeave() {
      this.isDragOver = false;
    },
    handleFileDrop(event) {
      this.isDragOver = false;
      const droppedFiles = Array.from(event.dataTransfer.files);

      const acceptList = this.accept.split(',').map((type) => type.trim().toLowerCase());

      const isAccepted = (file) => {
        const fileName = file.name.toLowerCase();
        const fileType = file.type.toLowerCase();

        return acceptList.some((accept) => {
          // Handle MIME type patterns like "image/*"
          if (accept.includes('*')) {
            const [category] = accept.split('/');
            return fileType.startsWith(`${category}/`);
          }

          // Handle full MIME types like "image/jpeg"
          if (accept.includes('/')) {
            return fileType === accept;
          }

          // Fallback to extension matching for formats like ".pdf"
          return fileName.endsWith(accept);
        });
      };

      const validFiles = droppedFiles.filter(isAccepted);
      const invalidFiles = droppedFiles.filter((file) => !isAccepted(file));

      if (invalidFiles.length) {
        alert(`Niektóre pliki mają nieprawidłowy format. Dozwolone: ${this.accept}`);
      }

      if (validFiles.length) {
        this.addFile(validFiles);
      }
    },
    addFile(newFiles) {
      if (this.isRequiredErrorMessageShown) {
        this.isRequiredErrorMessageShown = false;
      }
      const fileMap = new Map(this.files.map((file) => [file.name, file]));
      newFiles.forEach((file) => fileMap.set(file.name, file));
      this.$emit('update:files', Array.from(fileMap.values()));
    },
    removeFile(fileToRemove) {
      this.$emit(
        'update:files',
        this.files.filter((file) => file.name !== fileToRemove.name)
      );
    },
    downloadFile(file) {
      this.$emit('downloadFile', file);
    },
  },
};
</script>

<style lang="scss" scoped>
.file-input {
  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;
    text-align: center;
    row-gap: 16px;
    background: #f5f5f5;
    border: 1px dashed #e0e0e0;
    border-radius: 8px;
  }
}

.drag-over {
  border-color: #007bff;
  background-color: rgba(0, 123, 255, 0.1);
}

.visually-hidden {
  height: 1px;
  width: 1px;
  padding: 0;
  margin: 0;

  &.error--text {
    height: auto;
    width: auto;
  }

  ::v-deep.v-input__prepend-outer,
  ::v-deep.v-input__slot {
    display: none;
  }
}
</style>
